import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

const money = (n) => `${Number(n || 0).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
const iso = (d) => d.toLocaleDateString('en-CA');
const FORMATS = [{ id: 'xlsx', label: 'Excel' }, { id: 'csv', label: 'CSV' }, { id: 'pdf', label: 'PDF' }, { id: 'json', label: 'JSON' }];
const DEFAULT_SOURCES = ['transport_invoices', 'atelier_invoices', 'payments', 'expenses', 'supplier_invoices'];

const periods = () => {
  const n = new Date(), y = n.getFullYear(), m = n.getMonth(), q = Math.floor(m / 3) * 3;
  return {
    month: [iso(new Date(y, m, 1)), iso(n)],
    last_month: [iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))],
    quarter: [iso(new Date(y, q, 1)), iso(n)],
    year: [iso(new Date(y, 0, 1)), iso(n)],
  };
};
const PERIOD_LABELS = { month: 'Ce mois', last_month: 'Mois dernier', quarter: 'Ce trimestre', year: 'Cette année', custom: 'Autre' };

const Pill = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? 'bg-amber-500 text-black border-amber-500' : 'border-slate-700 text-slate-300 hover:border-slate-500'}`}>
    {children}
  </button>
);

const Toggle = ({ checked, onChange, label, testId }) => (
  <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="accent-amber-500 w-4 h-4" data-testid={testId} />
    {label}
  </label>
);

const PreviewSummary = ({ preview, onlyNew }) => (
  <div className="space-y-2" data-testid="export-preview">
    {preview.sources.map(s => (
      <div key={s.source} className="flex items-center justify-between text-sm" data-testid={`preview-${s.source}`}>
        <span className="text-slate-300">{s.label}</span>
        <span className="text-slate-400">
          {onlyNew ? `${s.new} nouveau(x) / ${s.count}` : `${s.count} document(s)`} · <span className="text-white">{money(s.total)}</span>
        </span>
      </div>
    ))}
    <div className="flex items-center justify-between pt-2 border-t border-slate-800 text-sm">
      <span className="text-white font-semibold" data-testid="preview-selected">{preview.selected} document(s) à exporter</span>
      {preview.journal.balanced ? (
        <span className="flex items-center gap-1 text-emerald-400 text-xs" data-testid="preview-balanced"><CheckCircle2 size={14} /> Écritures équilibrées ({money(preview.journal.debit)})</span>
      ) : (
        <span className="flex items-center gap-1 text-red-400 text-xs" data-testid="preview-unbalanced"><AlertTriangle size={14} /> Débit {money(preview.journal.debit)} ≠ crédit {money(preview.journal.credit)}</span>
      )}
    </div>
  </div>
);

const download = (res) => {
  const cd = res.headers['content-disposition'] || '';
  const name = (cd.match(/filename="([^"]+)"/) || [])[1] || 'export';
  const href = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = href; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(href), 10000);
};

export const ExportPanel = ({ baseUrl, headers, onExported, refreshKey, fixedRange }) => {
  const [meta, setMeta] = useState(null);
  const [sources, setSources] = useState(DEFAULT_SOURCES);
  const [period, setPeriod] = useState('month');
  const [ownRange, setRange] = useState(periods().month);
  const range = fixedRange || ownRange;
  const [templateId, setTemplateId] = useState('comptable');
  const [format, setFormat] = useState('xlsx');
  const [onlyNew, setOnlyNew] = useState(true);
  const [mark, setMark] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const hdr = useMemo(() => headers, [JSON.stringify(headers)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    axios.get(`${baseUrl}/templates`, { headers: hdr }).then(r => setMeta(r.data)).catch(() => toast.error('Modèles indisponibles'));
  }, [baseUrl, hdr]);

  const payload = { sources, date_from: range[0], date_to: range[1], template_id: templateId, format, only_new: onlyNew, mark_exported: mark };

  useEffect(() => {
    if (!sources.length || !range[0] || !range[1]) { setPreview(null); return; }
    axios.post(`${baseUrl}/preview`, payload, { headers: hdr }).then(r => setPreview(r.data)).catch(() => setPreview(null));
  }, [baseUrl, hdr, sources.join(','), range[0], range[1], onlyNew, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickPeriod = (p) => { setPeriod(p); if (p !== 'custom') setRange(periods()[p]); };
  const toggleSource = (s) => setSources(cur => cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s]);

  const run = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${baseUrl}/export`, payload, { headers: hdr, responseType: 'blob' });
      download(res);
      toast.success(mark ? 'Export téléchargé — documents marqués comme exportés' : 'Export téléchargé');
      axios.post(`${baseUrl}/preview`, payload, { headers: hdr }).then(r => setPreview(r.data)).catch(() => {});
      onExported?.();
    } catch { toast.error("L'export a échoué"); }
    setBusy(false);
  };

  if (!meta) return <p className="text-slate-500 text-sm">Chargement…</p>;
  const template = meta.templates.find(t => t.id === templateId);
  const inputCls = 'bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white';

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-5" data-testid="export-panel">
      <div className="flex items-center gap-2">
        <FileSpreadsheet size={18} className="text-amber-400" />
        <h3 className="text-white font-semibold">Exporter les données comptables</h3>
      </div>

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-slate-500">Données</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(meta.sources).map(([id, label]) => (
            <Pill key={id} active={sources.includes(id)} onClick={() => toggleSource(id)} testId={`export-source-${id}`}>{label}</Pill>
          ))}
        </div>
        {sources.includes('inventory_movements') && <p className="text-xs text-slate-500">Mouvements d'inventaire : consultation seulement, ils ne créent pas d'écriture au journal.</p>}
      </div>

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-slate-500">Période</p>
        {fixedRange ? (
          <p className="text-sm text-slate-300" data-testid="export-fixed-period">Du {range[0]} au {range[1]} <span className="text-slate-500">(période choisie en haut de la page)</span></p>
        ) : (
        <div className="flex flex-wrap items-center gap-2">
          {Object.entries(PERIOD_LABELS).map(([id, label]) => (
            <Pill key={id} active={period === id} onClick={() => pickPeriod(id)} testId={`export-period-${id}`}>{label}</Pill>
          ))}
          {period === 'custom' && (
            <span className="flex items-center gap-2 text-sm text-slate-400">
              <input type="date" value={range[0]} onChange={e => setRange([e.target.value, range[1]])} className={inputCls} data-testid="export-from" />
              au <input type="date" value={range[1]} onChange={e => setRange([range[0], e.target.value])} className={inputCls} data-testid="export-to" />
            </span>
          )}
        </div>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-slate-500">Destination (modèle)</p>
          <select value={templateId} onChange={e => setTemplateId(e.target.value)} className={`${inputCls} w-full`} data-testid="export-template-select">
            {meta.templates.map(t => <option key={t.id} value={t.id}>{t.name}{t.builtin ? '' : ' (perso)'}</option>)}
          </select>
          {template && <p className="text-xs text-slate-500">{template.mode === 'journal' ? 'Écritures débit / crédit avec numéros de compte' : 'Une ligne par document'}</p>}
        </div>
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-slate-500">Format</p>
          <div className="flex gap-2">
            {FORMATS.map(f => <Pill key={f.id} active={format === f.id} onClick={() => setFormat(f.id)} testId={`export-format-${f.id}`}>{f.label}</Pill>)}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-6">
        <Toggle checked={onlyNew} onChange={setOnlyNew} label="Seulement ce qui n'a pas encore été exporté" testId="export-only-new" />
        <Toggle checked={mark} onChange={setMark} label="Marquer comme exporté" testId="export-mark" />
      </div>

      {preview && <PreviewSummary preview={preview} onlyNew={onlyNew} />}

      <button onClick={run} disabled={busy || !sources.length || !preview?.selected} data-testid="export-run-button"
        className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-500 text-black font-semibold text-sm hover:bg-amber-400 disabled:opacity-40 transition-colors">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Télécharger l'export
      </button>
    </div>
  );
};
